import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Divider,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSuppliersService } from "../../Services";
import { Supplier } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface SupplierModalProps {
  open: boolean;
  onClose: () => void;
}

export const SupplierModal: React.FC<SupplierModalProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const {
    suppliersQuery,
    createSupplierMutation,
    updateSupplierMutation,
    deleteSupplierMutation,
  } = useSuppliersService({
    fetchSuppliersEnabled: open,
  });

  const resetState = () => {
    setIsAdding(false);
    setEditingSupplier(null);
    form.resetFields();
  };

  const handleEdit = (record: Supplier) => {
    setEditingSupplier(record);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone || "",
      email: record.email || "",
      address: record.address || "",
    });
    setIsAdding(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingSupplier) {
        updateSupplierMutation.mutate(
          { id: editingSupplier.id, ...values },
          {
            onSuccess: (res) => {
              if (res?.success) {
                message.success("Supplier updated successfully!");
                resetState();
              } else {
                message.error(res?.message || "Failed to update supplier.");
              }
            },
            onError: () => message.error("Something went wrong."),
          },
        );
      } else {
        createSupplierMutation.mutate(values, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Supplier added successfully!");
              resetState();
            } else {
              message.error(res?.message || "Failed to save supplier.");
            }
          },
          onError: () => message.error("Something went wrong."),
        });
      }
    } catch {
      message.error("Please fill all required fields correctly.");
    }
  };

  const handleDelete = (record: Supplier) => {
    deleteSupplierMutation.mutate(record.id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Supplier deleted successfully!");
        } else {
          message.error(res?.message || "Failed to delete supplier.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const columns = [
    {
      title: "Supplier Name",
      dataIndex: "name",
      key: "name",
      render: (v: string) => (
        <span className="font-semibold text-gray-700">{v}</span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (v: string) => v || <span className="text-gray-300">-</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v: string) => v || <span className="text-gray-300">-</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: Supplier) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this supplier?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const footerActions = isAdding
    ? [
        { label: "Cancel", onClick: resetState },
        {
          label: editingSupplier ? "Save Changes" : "Save Supplier",
          type: "primary" as const,
          onClick: handleSubmit,
          loading:
            createSupplierMutation.isPending ||
            updateSupplierMutation.isPending,
        },
      ]
    : [{ label: "Close", onClick: onClose }];

  return (
    <Modal
      title={modalTitle(
        isAdding
          ? editingSupplier
            ? "Edit Supplier"
            : "Add New Supplier"
          : "Manage Suppliers",
      )}
      open={open}
      onCancel={() => {
        resetState();
        onClose();
      }}
      footer={modalFooter({ actions: footerActions })}
      width={650}
    >
      {isAdding ? (
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="name"
            label="Supplier Name"
            rules={[{ required: true, message: "Supplier name is required" }]}
          >
            <Input placeholder="Enter supplier or company name" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="phone" label="Phone Number">
              <Input placeholder="e.g. +947xxxxxxxx" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email Address"
              rules={[{ type: "email", message: "Please enter a valid email" }]}
            >
              <Input placeholder="e.g. info@company.com" />
            </Form.Item>
          </div>
          <Form.Item name="address" label="Postal Address">
            <Input.TextArea
              placeholder="Enter address details"
              rows={2}
              style={{ resize: "none" }}
            />
          </Form.Item>
        </Form>
      ) : (
        <>
          <div className="my-4 flex justify-end">
            <Button
              type="primary"
              size="middle"
              icon={<PlusOutlined />}
              onClick={() => setIsAdding(true)}
            >
              Add Supplier
            </Button>
          </div>
          <Divider style={{ margin: "12px 0" }} />
          <Table
            dataSource={suppliersQuery.data || []}
            columns={columns}
            rowKey="id"
            size="small"
            loading={suppliersQuery.isLoading}
            pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
            bordered
            scroll={{ x: 500 }}
          />
        </>
      )}
    </Modal>
  );
};
